import { IInventoryPurchaseOrderListSearchPropsBody } from "@/api_framework/api_modals/Inventory";
import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useCouponCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponCategoryAPI";
import { useInventoryPurchaseOrderListFilterAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryPurchaseOrderListFilterAPI";
import { useInventoryVendorListSearchAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryVendorListSearchAPI";
import { usePurchaseOrderStatusListAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePurchaseOrderStatusListAPI";
import { useClinicTamsFilterAPI } from "@/app/hooks/api_hooks/Inventory/useClinicTamsFilterAPI";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { IInventoryState, setPurchaseOrderFilter } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

export interface IInventoryFilterProps {
  city: string;
  clinic: Array<string>;
  vendor: string;
  status: Array<string>;
  statusId: Array<string>;
  startDate: string;
  endDate: string;
  vendorName?: string;
  variant_category_name?: string;
  variant_category_id?: string;
}

export interface VendorSectionSearchResultType {
  name: string;
  vendor_id: string;
}

const OrderPurchaseFilterComponent = () => {
  const { getRouteKey } = useRouter();
  const dispatch = useAppDispatch();
  const { routeState } = useRouteInfo(getRouteKey("INVENTORY", "id"));
  const { cities, purchaseOrderFilter } = routeState.state as IInventoryState;
  const fetchOrgCities = useOrgCitiesListAPI("INVENTORY");
  const { execute: fetchClinicsTams, clinicList } = useClinicTamsFilterAPI();
  const { execute: fetchOrderStatus, purchaseOrderStatus } = usePurchaseOrderStatusListAPI();
  const fetchProductorderList = useInventoryPurchaseOrderListFilterAPI();
  const [initialFetch, setInitialFetch] = useState(false);

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 30);

  const [selectionDate, setSelectionDate] = useState({
    startDate: yesterday,
    endDate: today,
    key: "selectionDate",
  });
  const [_vendorSearchError, setVendorSearchError] = useState(false);
  const startDate = new Date(selectionDate.startDate);
  const endDate = new Date(selectionDate.endDate);
  const startYear = startDate.getFullYear();
  const startMonth = (startDate.getMonth() + 1).toString().padStart(2, "0");
  const startDay = startDate.getDate().toString().padStart(2, "0");
  const endYear = endDate.getFullYear();
  const endMonth = (endDate.getMonth() + 1).toString().padStart(2, "0");
  const endDay = endDate.getDate().toString().padStart(2, "0");
  const formattedStartDate = `${startYear}-${startMonth}-${startDay}`;
  const formattedEndDate = `${endYear}-${endMonth}-${endDay}`;
  const cityItems = useSelectMenuReducer(cities, "name", "id");
  const purchaseOrderStatusItem = useSelectMenuReducer(purchaseOrderStatus, "status_name", "status_id");
  const clinicItems = useSelectMenuReducer(clinicList, "display_name", "id");
  const { execute: fetchCategory } = useCouponCategoryAPI();
  const { execute: searchInSection } = useInventoryVendorListSearchAPI();

  const { mappedCityId, mappedClinicId } = useAppSelector((state) => state.root.UIStates);

  const formHook = useForm<IInventoryFilterProps>({
    mode: "onChange",
    defaultValues: {
      vendorName: "",
      clinic: [mappedClinicId],
      city: mappedCityId,
    },
  });

  // Fetch City and other filter dropdown api
  useEffect(() => {
    fetchOrgCities();
    fetchOrderStatus();
    fetchCategory();
  }, []);

  // Fetch Clinics
  useEffect(() => {
    const cityId = formHook.getValues("city");

    if (cityId) {
      fetchClinicsTams(cityId);
      // fetchOrgClinics({
      //   cityIds: [cityId],
      //   withProviders: false,
      //   clinicOptions: {
      //     selectFirst: true,
      //   },
      // });
    }
  }, [formHook.watch("city")]);

  const transformArray = (arr) => {
    return arr?.map((item) => item.replace(/ /g, "_"));
  };

  // useEffect(() => {
  //   if (cities?.length > 0) {
  //     formHook.setValue("city", cities[0]?.id);
  //   }
  // }, [cities?.length > 0]);

  // useEffect(() => {
  //   if (clinicList?.length > 0) {
  //     formHook.setValue("clinic", [clinicList[0]?.id]);
  //   }
  // }, [clinicList?.length > 0]);

  useEffect(() => {
    if (purchaseOrderFilter) {
      formHook.setValue("clinic", purchaseOrderFilter.clinic_ids || []);
      formHook.setValue("startDate", purchaseOrderFilter.start_date || "");
      formHook.setValue("endDate", purchaseOrderFilter.end_date || "");
      formHook.setValue("status", purchaseOrderFilter.po_statuses || []);
      formHook.setValue("vendor", purchaseOrderFilter.vendor_id || "");
      formHook.setValue("variant_category_id", purchaseOrderFilter.variant_category_id || "");
    }
  }, [purchaseOrderFilter, formHook]);

  useEffect(() => {
    const data = formHook.getValues();
    if (data?.city && data?.clinic?.length > 0 && data?.status?.length > 0 && !initialFetch) {
      const constuctPurchaseOrderFilterBody: IInventoryPurchaseOrderListSearchPropsBody = {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        clinic_ids: formHook.getValues("clinic"),
        vendor_id: formHook.getValues("vendor") === "" ? null : formHook.getValues("vendor"),
        po_statuses: transformArray(formHook.getValues("status")),
      };
      fetchProductorderList(constuctPurchaseOrderFilterBody);
      setInitialFetch(true);
    }
  }, [formHook.watch("clinic"), formHook.watch("vendor"), formHook.watch("status")]);

  /* Actions and Handlers */
  const validateConditionalFormFields = (data: IInventoryFilterProps) => {
    let isValid = false;
    if (data?.city !== "" || data?.clinic?.length !== 0 || data.vendor !== "") {
      isValid = true;
    }
    return isValid;
  };

  const Submit = (data: IInventoryFilterProps, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (data.vendorName === "") {
      setVendorSearchError(true);
    }
    const isFormSubmissionValid = validateConditionalFormFields(data);
    if (!isFormSubmissionValid) {
      return;
    }
    if (data && isFormSubmissionValid) {
      const constuctPurchaseOrderFilterBody: IInventoryPurchaseOrderListSearchPropsBody = {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        clinic_ids: data.clinic,
        vendor_id: data.vendor === "" ? null : data.vendor,
        po_statuses: transformArray(data.status),
      };
      fetchProductorderList(constuctPurchaseOrderFilterBody);
      dispatch(setPurchaseOrderFilter({ data: constuctPurchaseOrderFilterBody }));
    }
  };

  useEffect(() => {
    const vendor = formHook.getValues("vendor");
    if (vendor) {
      setVendorSearchError(false);
    }
  }, [formHook.watch("vendor")]);

  return (
    <div className="p-2 rounded-xl">
       <div className="justify-start text-black text-base font-semibold tracking-wide">Purchase Order</div>
      <form onSubmit={formHook.handleSubmit(Submit)}>
        <div className="flex flex-wrap gap-3 w-full mt-3">
          <div className="w-full sm:w-[48%] lg:w-auto">
            <OMCalendarComponent
              // label="Date Range"
              selectionDate={selectionDate}
              setSelectionDate={setSelectionDate}
              startDate={formattedStartDate}
              endDate={formattedEndDate}
              containerClassName="w-full"
              handelClick={() => {
                // console.log();
              }}
            />
          </div>
          <div className="w-[48%] sm:w-[48%] lg:w-auto">
            <SearchableSelectMenu
              // label="Vendor"
              placeholder="Select Vendor"
              errorMessages={[
                {
                  type: "required",
                  message: "Required",
                },
              ]}
              fieldError={formHook?.formState?.errors?.vendor ?? null}
              register={formHook.register("vendor", {
                required: false,
              })}
              onSelectItem={(item) => {
                formHook.setValue("vendorName", item.title);
                formHook.setValue("vendor", item.id);
              }}
              withAPISearch={{
                apiCallback: (searchID) => searchInSection(searchID, "PO_PAGE_SEARCH"),
                reducerIdKey: "vendor_id",
                reducerTitleKey: "trade_name",
              }}
              defaultSelectedItemForAPISearch={{
                id: formHook.watch("vendor"),
                title: formHook.watch("vendorName"),
              }}
              showDropdownIcon
            />
          </div>
          <div className="w-[48%] sm:w-[48%] lg:w-auto">

            <SearchableMultiSelectMenu
              withSelectAll
              defaultSelectAll
              // label="PO Status"
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  const statusName = item.map((i) => i.title);
                  const statusId = item.map((i) => i.id);
                  formHook.setValue("status", statusName);
                  formHook.setValue("statusId", statusId);
                }
              }}
              fieldError={formHook.formState.errors.status}
              register={formHook.register("status", { required: true })}
              selectItems={purchaseOrderStatusItem}
              placeholder="Select Status"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={purchaseOrderStatusItem?.filter((c) => formHook.watch("statusId")?.includes(c.id))}
              className="text-gray-800"
            />
          </div>
          <div className="w-[48%] sm:w-[48%] lg:w-auto">
            <SearchableSelectMenu
              errorMessages={[]}
              // label="City"
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("city", item.id);
                }
              }}
              fieldError={formHook.formState.errors.city}
              register={formHook.register("city", { required: true })}
              selectItems={cityItems}
              placeholder="Select City"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={cityItems?.filter((ci) => ci.id === formHook.watch("city"))[0]}
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="w-[48%] sm:w-[48%] lg:w-auto">
            <SearchableMultiSelectMenu
              withSelectAll
              // label="Select Clinic"
              // defaultSelectAll
              errorMessages={[{ type: "required", message: "Clinic is required" }]}
              onSelectItem={(item) => {
                if (item) {
                  const clincId = item.map((i) => i.id);
                  formHook.setValue("clinic", clincId);
                }
              }}
              fieldError={formHook.formState.errors.clinic}
              register={formHook.register("clinic", { required: true })}
              selectItems={clinicItems}
              placeholder="Clinic"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={clinicItems?.filter((c) => formHook.watch("clinic")?.includes(c.id))}
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div>
            <button
              type="submit"
              className="bg-black px-6 py-2 border-transparent rounded-lg text-sm text-white cursor-pointer"
            >
              Search
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
export default OrderPurchaseFilterComponent;
